import { rollbarError } from "./rollbarErrors";
import { SilverseaHelpers } from "./helpers";
import { ContentInteraction } from "./interfaces";
import { NativeAnalytics } from "./analytics_providers/nativeAnalytics";
import { Matomo } from "./analytics_providers/matomo";
// import { GoogleAds, GoogleAnalytics } from "./analytics_providers/google";
import { GoogleAnalytics } from "./analytics_providers/google";
import { Floodlight } from "./analytics_providers/floodlight";
import { ConversionEventDetail } from "./analytics_providers/analyticsProviderBase";
import { ConsentSettings } from "./consentSettings";
import CRC32 from "crc-32";
import { isbot } from "isbot";

interface AnalyticsProviders {
  matomo: Matomo;
  // oogle_ads: GoogleAds;
  google_analytics: GoogleAnalytics;
  native_analytics: NativeAnalytics;
  floodlight?: Floodlight;
}

interface IDMap {
  AUID: string;
  id_hash: number;
  map: {
    matomo_id?: unknown;
    google_id?: unknown;
    adobe_id?: string;
    silversea_individual_id?: string;
    privacy_settings: {
      essential?: boolean;
      performance?: boolean;
      functional?: boolean;
      targeting?: boolean;
      attitude?: string;
    };
  };
}

interface ConversionEvent {
  detail: ConversionEventDetail;
}

export class SilverseaVisitor extends SilverseaHelpers {
  private _auid: string = this.get_stored_auid() || this.generate_auid();
  private _session_auid: string = this.get_stored_session_auid() || this.generate_session_auid();
  pageloads_done = 0;

  providers: AnalyticsProviders = {
    matomo: new Matomo(),
    google_analytics: new GoogleAnalytics(),
    // google_ads: new GoogleAds(),
    native_analytics: new NativeAnalytics(),
    floodlight: undefined
  };
  onetrust_consent: ConsentSettings = new ConsentSettings();
  known_url: string = window.location.href;

  constructor(use_floodlight_test_template = false) {
    super();

    try {
      this.providers.floodlight = new Floodlight(use_floodlight_test_template);
    } catch (e) {
      rollbarError("Floodlight INIT", e);
    }

    window.dataLayer = window.dataLayer || [];
    window._mtm = window._mtm || [];
    window._paq = window._paq || [];
    _paq.push(["alwaysUseSendBeacon"]);

    this.set_session_cookie();
  }

  private set_session_cookie(): void {
    this.set_cookie(
      "silversea_session_auid",
      this._session_auid,
      1
    );
  }

  log(context, message): void {
    message = message || "";
    if (window.ss_show_logs) {
      console.log(
        "%c%s%cSilverseaVisitor-> %c%s",
        "background-color: darkorange; padding: 0.5em;",
        String.fromCharCode(187),
        "background-color: #323234; color: #fcfcfd; padding: 0.5em",
        "",
        context,
        message
      );
    }
  }

  list_provider_statuses(): { [key: string]: boolean } {
    const l = {};
    try {
      for (const key of Object.keys(this.providers)) {
        l[key] = this.providers[key].has_started;
      }
    } catch (e) {
      rollbarError("List Providers is_ready", e);
    }
    return l;
  }

  async post_consent_init(): Promise<void> {
    // push to the datalayer

    const starting_providers = [];
    for (const key of Object.keys(this.providers)) {
      starting_providers.push(
        new Promise((resolve) => {
          this.providers[key].start(this.onetrust_consent);
          resolve(true);
        })
      );
    }

    await Promise.allSettled(starting_providers);

    // this.log("starting auid_pipeline_run", "");
    // sv.auid_pipeline_run();

    // // DataLayer Subscribers
    // wspr.S("pageload", "analytics_to_datalayer", async (wspr_event) => {
    //   const event = wspr_event.m;
    //   sv.trigger_event("pageload", undefined);
    //   return true;
    // });
    // wspr.S(
    //   "web_request_submitted",
    //   "analytics_to_datalayer",
    //   async (wspr_event) => {
    //     const event = wspr_event.m;
    //     sv.trigger_event("conversion > " + event.request_type, event);
    //     return true;
    //   }
    // );
    // wspr.S(
    //   "click_interaction",
    //   "analytics_to_datalayer",
    //   async (wspr_event) => {
    //     this.log("analytics_to_datalayer -> CLICK", "");
    //     const data_content_attribs = wspr_event.m;
    //     this.trigger_event("data-track-content", data_content_attribs);
    //     return true;
    //   }
    // );
    // wspr.S("cruise_filters_changed", "analytics_to_datalayer", (wspr_event) => {
    //   const event = wspr_event.m;
    //   const context = "cruise-filters-changed";
    //   this.debug(context + " -> CRUISE_FILTER_CHANGED", event.detail);
    //   this.trigger_event("cruise-filters-changed", event.detail);
    //   return true;
    // });

    // wspr.S("gtag", "trigger_gtag", (wspr_event) => {
    //   const message = wspr_event.m;
    //   gtag(...message);
    //   return true;
    // });

    // this.log("pageload " + pageload_num + " success...", "");
    // pageload logic done
  }

  generate_auid(): string {
    return "".concat(Date.now().toString(), "-", this.uuidv4());
  }

  // required by Gatsby, do not remove!
  get auid(): string {
    return this._auid || "";
  }

  get_auid(): string {
    return this.auid;
  }

  get_stored_auid(): string {
    const existing_cookie_value = this.get_cookie("silversea_core_auid");
    if (existing_cookie_value) {
      const stored_AUID = JSON.parse(existing_cookie_value)["AUID"];
      if (
        stored_AUID.match(
          /^[-]{0,1}[0-9]{1,13}-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/g
        ) !== null
      ) {
        return stored_AUID;
      }
    }
  }

  generate_session_auid(): string {
    return this.uuidv4();
  }

  get_session_auid(): string {
    return this._session_auid;
  }

  get_stored_session_auid(): string {
    const existing_cookie_value = this.get_cookie("silversea_session_auid");
    if (existing_cookie_value) {
      if (
        existing_cookie_value.match(
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        ) !== null
      ) {
        return existing_cookie_value;
      }
    }
    return null;
  }

  async get_id_map(): Promise<IDMap> {
    // --- get analytics id"s ----

    const prom_ids = await Promise.allSettled([
      // Removed because Universal Analytics has been deprecated
      // this.providers.google_analytics.get_visitor_id("UA-5494850-14"),
      this.providers.google_analytics.get_visitor_id("G-ZXCJY21NXY"),
      this.providers.matomo.get_visitor_id()
    ]);

    // googlez
    const google_visitor_id =
      // prom_ids[0]["value"] ||
      prom_ids[0]["value"] ||
      //this.providers.google_analytics.get_visitor_id_sync() ||
      null;

    // matomo
    const matomo_visitor_id = prom_ids[1]["value"] || null;

    // IndividualID
    let silversea_individual_id = null;
    try {
      silversea_individual_id =
        this.get_cookie("tmsdbee") ||
        this.get_my_silversea_vals()?.individual_id ||
        null;
      if (
        silversea_individual_id &&
        (silversea_individual_id == "null" || "0")
      ) {
        silversea_individual_id = null;
      }
    } catch (error) {
      // do nothing
    }

    // OneTrust consent
    let otc = null;
    try {
      otc = JSON.parse(this.onetrust_consent.get_consent_json());
    } catch (error) {
      // default
      otc = {
        essential: null,
        performance: null,
        functional: null,
        targeting: null
      };
    }

    const analytics_ids = [
      matomo_visitor_id,
      google_visitor_id,
      JSON.stringify(otc),
      silversea_individual_id
    ];

    const id_hash = CRC32.str(analytics_ids.join("&"));

    return {
      AUID: this.auid,
      id_hash: id_hash,
      map: {
        matomo_id: matomo_visitor_id,
        google_id: google_visitor_id,
        adobe_id: null,
        silversea_individual_id: silversea_individual_id,
        privacy_settings: otc
      }
    };
  }

  async send_id_map_to_datalanding(id_map: IDMap): Promise<unknown> {
    async function send(id_map): Promise<string> {
      let api_path = "https://qa-api2.digital.silversea.com/auidApiSubmission";
      if (
        window.location.href.startsWith("https://www.silversea.com") ||
        window.location.href.startsWith("https://discover.silversea.com") ||
        window.location.href.startsWith(
          "https://antarcticabridge.silversea.com"
        ) ||
        window.location.href.startsWith("https://quote.silversea.com") ||
        window.location.href.startsWith("https://my.silversea.com") ||
        window.location.href.startsWith("https://booking.silversea.com")
      ) {
        api_path = "https://api2.digital.silversea.com/auidApiSubmission";
      }

      const response = await fetch(api_path, {
        method: "post",
        body: JSON.stringify(id_map),
        headers: { "Content-Type": "application/json" }
      });

      const responseData = await response.text();

      if (response.ok) {
        console.log(
          `AUID sent successfully: ${response.status} ${responseData}`
        );
        return responseData;
      } else {
        throw new Error(
          `HTTP Error Response: ${response.status} ${responseData}`
        );
      }
    }

    return this.retry_func(send, id_map, '"Send AUID"', 3000, 5);
  }

  set_id_map_transaction(id_map: IDMap, data_has_sent: boolean): void {
    const id_map_cookie = {
      AUID: this.auid,
      id_hash: id_map.id_hash,
      sent: data_has_sent
    };
    this.set_cookie(
      "silversea_core_auid",
      JSON.stringify(id_map_cookie),
      365 * 2
    );
  }

  async auid_pipeline_run(): Promise<void> {
    // this.log("this.auid_pipeline_run() starting...")

    if (typeof navigator !== "undefined" && isbot(navigator.userAgent)) {
      this.log("AUID pipeline skipped: bot detected", navigator.userAgent);
      return;
    }

    if (!this.can_write_cookies()) {
      this.log("this.auid_pipeline_run() CAN NOT write cookies...", "");
      return null;
    }

    this.log("this.auid_pipeline_run() CAN write cookies...", "");

    const id_map = await this.get_id_map();

    this.log("id_map", id_map);

    // --- if new message needed, do it ---

    if (this.auid_state_is_new(id_map.id_hash)) {
      this.log("New AUID content detected. Sending...", "");
      this.set_id_map_transaction(id_map, false);
      const response = await this.send_id_map_to_datalanding(id_map);
      if (response == "OK") {
        this.set_id_map_transaction(id_map, true);
        this.log("New AUID content sent...", "");
      } else {
        this.log("New AUID had an issue sending, response was: ", response);
      }
    }
  }

  set_conversion_path(): void {
    const page_type = this.get_page_type();
    if (
      page_type != "Thank You Page" &&
      page_type != "Contact Form" &&
      page_type != "Brochures Page"
    ) {
      this.set_cookie("conversion_path", window.location.href, 1);
      this.set_cookie("conversion_path_page_type", page_type, 1);
    }
  }

  // ----- BELOW HERE ARE HELPER FUNCTIONS (Not Exported) ----

  auid_state_is_new(id_hash: number): boolean {
    const existing_cookie_value = this.get_cookie("silversea_core_auid");
    const cookie_exists = existing_cookie_value !== undefined;
    const stored_AUID = this.get_stored_auid();

    if (cookie_exists) {
      const cookie_values = JSON.parse(existing_cookie_value);
      const stored_id_hash = cookie_values["id_hash"];
      const cookie_sent = cookie_values["sent"];

      if (
        stored_id_hash == id_hash &&
        cookie_sent &&
        stored_AUID != undefined
      ) {
        // this.log("AUID state is NOT new")
        return false;
      }
    }
    this.log("AUID state IS new", "");
    return true;
  }

  // async distribute_tracked_click_event(
  //   data_content_attribs: ContentInteraction
  // ): Promise<void> {
  //   this.log("CLICK!", "");

  //   const ps = [
  //     new Promise((resolve) => {
  //       // add to datalayer too
  //       this.trigger_event("data-track-content", data_content_attribs);
  //       resolve(true);
  //     }).catch((e) => {
  //       console.error(e);
  //       rollbarError("Distribute interaction_triggered (dataLayer)", e);
  //     })
  //   ];

  //   for (const key of Object.keys(this.providers)) {
  //     ps.push(
  //       new Promise((resolve) => {
  //         this.providers[key].interaction_triggered(data_content_attribs);
  //         resolve(true);
  //       }).catch((e) => {
  //         console.error(e);
  //         rollbarError("Distribute interaction_triggered (" + key + ")", e);
  //       })
  //     );
  //   }

  //   await Promise.allSettled(ps);
  // }

  //   async distribute_conversion_event(
  //     original_event: ConversionEvent
  //   ): Promise<void> {
  //     this.log("WEB_REQUEST_EVENT (original) > ", original_event.detail);
  //     const event = { ...original_event.detail };
  //     this.log("WEB_REQUEST_EVENT (copy) > ", event);

  //     // we need to repeat to synthesise a thank you page as thats what the marketing people are used to.
  //     const allowed_request_types = new Set([
  //       "RAQ",
  //       "RAB",
  //       "RAC",
  //       "EMA",
  //       "SFO",
  //       "SFB",
  //       "TYP",
  //       "WEBQ",
  //       "WBBK",
  //       "WBOF.01",
  //       "WBOF.02"
  //     ]);
  //     if (!allowed_request_types.has(event.request_type)) {
  //       throw new Error(
  //         "Unexpected Request Type in web-request-submitted: " +
  //           event.request_type
  //       );
  //     }

  //     const request_types = [event.request_type];

  //     // these do not have a thank you page
  //     const stop_typ_trigger = new Set(["WBOF.01", "WBOF.02"]);

  //     if (!stop_typ_trigger.has(event.request_type)) {
  //       request_types.push("TYP");
  //     }

  //     const ps = [];

  //     for (let i = 0, l = request_types.length; i < l; i++) {
  //       event.request_type = request_types[i];
  //       this.log("WEB_REQUEST_EVENT > ", event);

  //       this.log("DISTRIBUTING CONVERSION EVENT", "");

  //       for (const key of Object.keys(this.providers)) {
  //         ps.push(
  //           new Promise((resolve) => {
  //             this.log("DISTRIBUTING TO " + key, "");
  //             this.providers[key].conversion_triggered(event);
  //             resolve(true);
  //           }).catch((e) => {
  //             console.error(e);
  //             rollbarError("Distribute conversion_triggered (" + key + ")", e);
  //           })
  //         );
  //       }

  //       ps.push(
  //         new Promise((resolve) => {
  //           this.log("DISTRIBUTING TO dataLayer", "");
  //           // send to datalayer
  //           this.trigger_event("conversion > " + event.request_type, event);
  //           resolve(true);
  //         }).catch((e) => {
  //           console.error(e);
  //           rollbarError("Distribute conversion_triggered dataLayer", e);
  //         })
  //       );
  //     }

  //     await Promise.allSettled(ps);
  //   }
}
